/**
 * `region_norm` — RU federal-subject dictionary — T1.1c.
 *
 * Evidence (docs/02-data-profiles.md §10.3 + §11): THREE region taxonomies live side by side
 * in the same campaign and only 41 of 71 (ЕВИ) vs 50 (Квоты) spellings intersect verbatim:
 *
 *   1. ЕВИ / ЕПГУ / «На зачислении» — no «г.» prefix (`Москва`, `Республика Татарстан`).
 *   2. Квоты / «Наименование ДОП» — with «г.» prefix (`г. Москва`, `г. Санкт-Петербург`,
 *      `г. Севастополь`).
 *   3. Inside addresses — official + colloquial name in brackets, in three registers:
 *      `Республика Татарстан (Татарстан)` (ЕВИ, 29×),
 *      `Республика татарстан (татарстан)` (ЕПГУ 8.09, 170×),
 *      `Республика Татарстан` (Квоты, ДОП) — plus Адыгея in the same three shapes.
 *   4. Full vs short official form: `Кемеровская область - Кузбасс` (Квоты.Регион) vs
 *      `Кемеровская область - кузбасс` (inside an address) vs `Кемеровская область` (ЕВИ)
 *      vs the bare colloquial `Кузбасс`.
 *
 * This module owns the mapping. It is EXPORTED AS DATA so D2 (venue matching service) and
 * the D1 seed CLI can extend it without touching code: add `KEYS_TO_CANONICAL[fold(v)] = c`.
 *
 * Canonical spellings are the 2026 official Russian names of the federal subjects that
 * appear in docs/02. Keys are FOLDED forms — see `foldRegion` — so the dictionary itself
 * stays free of register, parenthetical and «г.» noise.
 */
import { RU_LOCALE, normalizeText } from './normalize';
/**
 * Variant → canonical. Keys are folded (`foldRegion`), i.e. lowercase, NFKC, ASCII hyphens,
 * no «г.» prefix, no trailing parenthetical, collapsed whitespace.
 */
export const KEYS_TO_CANONICAL = Object.freeze({
    // Республика Татарстан — docs/02 §10.3.3 (29 / 170 / N occurrences, three registers)
    'республика татарстан': 'Республика Татарстан',
    'татарстан': 'Республика Татарстан',
    // Республика Адыгея — docs/02 §10.3.3 (ЕВИ vs ЕПГУ 8.09 vs Квоты/ДОП)
    'республика адыгея': 'Республика Адыгея',
    'адыгея': 'Республика Адыгея',
    // Кемеровская область — docs/02 §10.3.4 (full / short / bare colloquial form)
    'кемеровская область - кузбасс': 'Кемеровская область - Кузбасс',
    'кемеровская область': 'Кемеровская область - Кузбасс',
    'кузбасс': 'Кемеровская область - Кузбасс',
    // «г.»-prefix split (docs/02 §10.3.5) — the keys below are already prefix-free
    'москва': 'Москва',
    'санкт-петербург': 'Санкт-Петербург',
    'севастополь': 'Севастополь',
    // Remaining subjects named in docs/02 (§1, §4, §5, §7 examples)
    'московская область': 'Московская область',
    'ульяновская область': 'Ульяновская область',
    'калужская область': 'Калужская область',
    'ставропольский край': 'Ставропольский край',
    'ярославская область': 'Ярославская область',
    'республика алтай': 'Республика Алтай',
    // T.Z179 (24.09) — the prod venues' regions the docs/02 evidence never named (48 of the
    // 59 in `venues` answered the folded key instead of the canonical name, so the dashboard's
    // region binding matched nothing after the regions were normalized to the official
    // spellings). Keys = the folded official names, values = the official spellings.
    'алтайский край': 'Алтайский край',
    'амурская область': 'Амурская область',
    'астраханская область': 'Астраханская область',
    'белгородская область': 'Белгородская область',
    'владимирская область': 'Владимирская область',
    'волгоградская область': 'Волгоградская область',
    'вологодская область': 'Вологодская область',
    'забайкальский край': 'Забайкальский край',
    'иркутская область': 'Иркутская область',
    'калининградская область': 'Калининградская область',
    'карачаево-черкесская республика': 'Карачаево-Черкесская Республика',
    'кировская область': 'Кировская область',
    'костромская область': 'Костромская область',
    'краснодарский край': 'Краснодарский край',
    'красноярский край': 'Красноярский край',
    'курганская область': 'Курганская область',
    'курская область': 'Курская область',
    'мурманская область': 'Мурманская область',
    'нижегородская область': 'Нижегородская область',
    'новгородская область': 'Новгородская область',
    'новосибирская область': 'Новосибирская область',
    'омская область': 'Омская область',
    'оренбургская область': 'Оренбургская область',
    'орловская область': 'Орловская область',
    'пензенская область': 'Пензенская область',
    'пермский край': 'Пермский край',
    'приморский край': 'Приморский край',
    'псковская область': 'Псковская область',
    'республика башкортостан': 'Республика Башкортостан',
    'республика бурятия': 'Республика Бурятия',
    'республика дагестан': 'Республика Дагестан',
    'республика карелия': 'Республика Карелия',
    'республика коми': 'Республика Коми',
    'республика марий эл': 'Республика Марий Эл',
    'республика мордовия': 'Республика Мордовия',
    'республика хакасия': 'Республика Хакасия',
    'ростовская область': 'Ростовская область',
    'свердловская область': 'Свердловская область',
    'свердловская обл': 'Свердловская область',
    'свердловская': 'Свердловская область',
    'самарская область': 'Самарская область',
    'саратовская область': 'Саратовская область',
    'тамбовская область': 'Тамбовская область',
    'тверская область': 'Тверская область',
    'тульская область': 'Тульская область',
    'тюменская область': 'Тюменская область',
    'удмуртская республика': 'Удмуртская Республика',
    'хабаровский край': 'Хабаровский край',
    'ханты-мансийский автономный округ - югра': 'Ханты-Мансийский автономный округ - Югра',
    'челябинская область': 'Челябинская область',
    'чеченская республика': 'Чеченская Республика',
});
/** Canonical names, deduplicated and sorted — the lookup set for `isKnownRegion`. */
export const CANONICAL_REGIONS = Object.freeze([...new Set(Object.values(KEYS_TO_CANONICAL))].sort((a, b) => a.localeCompare(b, RU_LOCALE)));
/**
 * Folds a region string to its dictionary key: NFKC, RU case fold, hyphen unification,
 * whitespace collapse, trailing parenthetical strip, «г.» prefix drop, edge punctuation trim.
 * Reuses the address pipeline so the two never drift apart; the result is a KEY (lowercase),
 * not a display name — `normalizeRegion` is what callers show.
 */
export function foldRegion(raw) {
    return normalizeText(raw);
}
/**
 * `region_norm` lookup: raw variant (any register, any of the three taxonomies, with or
 * without parenthetical / «г.» prefix) → canonical federal-subject name.
 *
 * Unknown input degrades gracefully and DETERMINISTICALLY: the folded key is returned, so
 * unseen spellings still group together (one variant == one key) and become a one-line
 * dictionary addition instead of a silent mismatch. `''` in → `''` out.
 */
export function normalizeRegion(raw) {
    const key = foldRegion(raw);
    if (key.length === 0)
        return '';
    return KEYS_TO_CANONICAL[key] ?? key;
}
/** True when the raw string resolves to a known canonical region. */
export function isKnownRegion(raw) {
    const key = foldRegion(raw);
    return key.length > 0 && KEYS_TO_CANONICAL[key] !== undefined;
}
/** Every dictionary variant that maps onto the given canonical name. */
export function regionVariants(canonical) {
    return Object.keys(KEYS_TO_CANONICAL).filter((key) => KEYS_TO_CANONICAL[key] === canonical);
}
export const REGION_VARIANT_EVIDENCE = Object.freeze([
    {
        raw: 'Республика Татарстан (Татарстан)',
        source: 'ЕВИ 9.09 / Регион',
        count: 29,
        docClass: '02 §10.3.3',
    },
    {
        raw: 'Республика татарстан (татарстан)',
        source: 'ЕПГУ 8.09 / Выбранный регион обучения',
        count: 170,
        docClass: '02 §10.3.3',
    },
    {
        raw: '423457, ул.Мира, д.10, Альметьевск, Республика татарстан (татарстан)',
        source: 'На зачислении 08.09 / Адрес площадки',
        count: null,
        docClass: '02 §10.3.3',
    },
    {
        raw: 'Республика Адыгея (Адыгея)',
        source: 'ЕВИ 9.09 / Регион',
        count: null,
        docClass: '02 §10.3.3',
    },
    {
        raw: 'Республика адыгея (адыгея)',
        source: 'ЕПГУ 8.09',
        count: null,
        docClass: '02 §10.3.3',
    },
    {
        raw: 'Республика Адыгея',
        source: 'Квоты, Наименование ДОП',
        count: null,
        docClass: '02 §10.3.3',
    },
    {
        raw: 'Кемеровская область - Кузбасс',
        source: 'Квоты / Регион',
        count: null,
        docClass: '02 §10.3.4',
    },
    {
        raw: 'Кемеровская область - кузбасс',
        source: 'Квоты / Адрес (внутри адреса)',
        count: null,
        docClass: '02 §10.3.4',
    },
    {
        raw: 'Кемеровская область',
        source: 'ЕВИ 9.09 / Регион',
        count: null,
        docClass: '02 §10.3.4',
    },
    { raw: 'г. Москва', source: 'Квоты / Регион', count: null, docClass: '02 §10.3.5' },
    { raw: 'г. Санкт-Петербург', source: 'Квоты / Регион', count: null, docClass: '02 §10.3.5' },
    { raw: 'г. Севастополь', source: 'Квоты / Регион', count: null, docClass: '02 §10.3.5' },
    { raw: 'Москва', source: 'ЕВИ 9.09 / Регион', count: null, docClass: '02 §10.3.5' },
]);
//# sourceMappingURL=regions.js.map